import React from 'react';
import { useTranslation } from 'react-i18next';
import { LeagueCreationData, ValidationError } from '../../../types/league';

interface StepProps {
  data: Partial<LeagueCreationData>;
  updateData: (data: Partial<LeagueCreationData>) => void;
  errors: ValidationError[];
  addError: (field: string, message: string) => void;
  clearErrors: () => void;
}

const ReviewStep: React.FC<StepProps> = ({ data }) => {
  const { t } = useTranslation();
  const getSquadName = (squadId: number): string => {
    const squad = data.squads?.find(s => s.id === squadId);
    return squad ? squad.name : `Squad ${squadId}`;
  };

  const getTranslatedLeaderboardName = (name: string): string => {
    // Translate leaderboard names from English values stored in data
    switch (name) {
      case 'Most Points':
        return t('admin.createLeague.steps.leaderboardStep.leaderboardTypes.mostPoints');
      case 'Overall Pinfall':
        return t('admin.createLeague.steps.leaderboardStep.leaderboardTypes.overallPinfall');
      case 'Overall Team Pinfall':
        return t('admin.createLeague.steps.leaderboardStep.leaderboardTypes.overallTeamPinfall');
      default:
        return name; // Return as-is for custom leaderboard names
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-white mb-2">{t('admin.createLeague.steps.reviewStep.title')}</h2>
        <p className="text-purple-200">
          {t('admin.createLeague.steps.reviewStep.subtitle')}
        </p>
      </div>

      {/* Basic Information */}
      <div className="bg-white/5 rounded-lg p-6 border border-white/10">
        <h3 className="text-lg font-semibold text-white mb-4">{t('admin.createLeague.steps.reviewStep.basicInformation')}</h3>
        <div className="grid md:grid-cols-2 gap-4 text-purple-200">
          <div>
            <span className="font-medium">{t('admin.createLeague.steps.reviewStep.leagueName')}</span> {data.name || t('admin.createLeague.steps.reviewStep.notSpecified')}
          </div>
          <div>
            <span className="font-medium">{t('admin.createLeague.steps.reviewStep.teamFormat')}</span> {data.teamFormat || t('admin.createLeague.steps.reviewStep.notSpecified')}
          </div>
          <div>
            <span className="font-medium">{t('admin.createLeague.steps.reviewStep.startDate')}</span> {data.startDate || t('admin.createLeague.steps.reviewStep.notSpecified')}
          </div>
          <div>
            <span className="font-medium">{t('admin.createLeague.steps.reviewStep.endDate')}</span> {data.endDate || t('admin.createLeague.steps.reviewStep.notSpecified')}
          </div>
          <div className="md:col-span-2">
            <span className="font-medium">{t('admin.createLeague.steps.reviewStep.description')}</span> {data.description || t('admin.createLeague.steps.reviewStep.notSpecified')}
          </div>
        </div>
      </div>

      {/* Rules */}
      {data.rules && (
        <div className="bg-white/5 rounded-lg p-6 border border-white/10">
          <h3 className="text-lg font-semibold text-white mb-4">{t('admin.createLeague.steps.reviewStep.leagueRules')}</h3>
          <p className="text-purple-200 whitespace-pre-wrap">{data.rules.description}</p>
        </div>
      )}

      {/* Handicap Configuration */}
      {data.handicapConfig && (
        <div className="bg-white/5 rounded-lg p-6 border border-white/10">
          <h3 className="text-lg font-semibold text-white mb-4">{t('admin.createLeague.steps.reviewStep.handicapConfiguration')}</h3>
          <div className="grid md:grid-cols-2 gap-4 text-purple-200">
            <div>
              <span className="font-medium">{t('admin.createLeague.steps.reviewStep.handicapType') || 'Handicap Type'}</span>{' '}
              {data.handicapConfig.handicap_type === 'team_difference' ? 'Team Difference' : 'Individual'}
            </div>
            {data.handicapConfig.handicap_type === 'team_difference' ? (
              <div>
                <span className="font-medium">{t('admin.createLeague.steps.reviewStep.teamDiffPercentage') || 'Team Difference %'}</span>{' '}
                {data.handicapConfig.team_difference_percentage ? `${data.handicapConfig.team_difference_percentage}%` : t('admin.createLeague.steps.reviewStep.notSet')}
              </div>
            ) : (<>
            <div>
              <span className="font-medium">{t('admin.createLeague.steps.reviewStep.baseAverage')}</span> {data.handicapConfig.base_average ?? t('admin.createLeague.steps.reviewStep.notSet')}
            </div>
            <div>
              <span className="font-medium">{t('admin.createLeague.steps.reviewStep.handicapPercentage')}</span> {data.handicapConfig.handicap_percentage ? `${data.handicapConfig.handicap_percentage}%` : t('admin.createLeague.steps.reviewStep.notSet')}
            </div>
            <div>
              <span className="font-medium">{t('admin.createLeague.steps.reviewStep.calculationFrequency')}</span> {
                data.handicapConfig.calculation_frequency === 'weekly' ? t('admin.createLeague.steps.reviewStep.frequencyOptions.weekly') :
                data.handicapConfig.calculation_frequency === 'monthly' ? t('admin.createLeague.steps.reviewStep.frequencyOptions.monthly') :
                data.handicapConfig.calculation_frequency === 'per_league' ? t('admin.createLeague.steps.reviewStep.frequencyOptions.per_league') :
                data.handicapConfig.calculation_frequency === 'yearly' ? t('admin.createLeague.steps.reviewStep.frequencyOptions.yearly') :
                t('admin.createLeague.steps.reviewStep.notSet')
              }
            </div>
            <div>
              <span className="font-medium">{t('admin.createLeague.steps.reviewStep.lockHandicapAfter')}</span> {data.handicapConfig.lock_handicap_after_weeks ? `${data.handicapConfig.lock_handicap_after_weeks} ${t('admin.createLeague.steps.reviewStep.weeks')}` : t('admin.createLeague.steps.reviewStep.notSet')}
            </div>
            
            {/* Maximum Handicap Caps - individual only */}
            {(data.handicapConfig.a_max || data.handicapConfig.b_max || 
              data.handicapConfig.c_max || data.handicapConfig.d_max || 
              data.handicapConfig.e_max || data.handicapConfig.league_max) && (
              <div className="md:col-span-2 mt-2">
                <span className="font-medium block mb-2">{t('admin.createLeague.steps.reviewStep.maxHandicapCaps')}</span>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                  {data.handicapConfig.a_max && (
                    <div className="bg-white/5 rounded px-3 py-1.5 border border-white/10">
                      {t('admin.createLeague.steps.reviewStep.divisionLabels.a')} <span className="text-white font-semibold">{data.handicapConfig.a_max}</span>
                    </div>
                  )}
                  {data.handicapConfig.b_max && (
                    <div className="bg-white/5 rounded px-3 py-1.5 border border-white/10">
                      {t('admin.createLeague.steps.reviewStep.divisionLabels.b')} <span className="text-white font-semibold">{data.handicapConfig.b_max}</span>
                    </div>
                  )}
                  {data.handicapConfig.c_max && (
                    <div className="bg-white/5 rounded px-3 py-1.5 border border-white/10">
                      {t('admin.createLeague.steps.reviewStep.divisionLabels.c')} <span className="text-white font-semibold">{data.handicapConfig.c_max}</span>
                    </div>
                  )}
                  {data.handicapConfig.d_max && (
                    <div className="bg-white/5 rounded px-3 py-1.5 border border-white/10">
                      {t('admin.createLeague.steps.reviewStep.divisionLabels.d')} <span className="text-white font-semibold">{data.handicapConfig.d_max}</span>
                    </div>
                  )}
                  {data.handicapConfig.e_max && (
                    <div className="bg-white/5 rounded px-3 py-1.5 border border-white/10">
                      {t('admin.createLeague.steps.reviewStep.divisionLabels.e')} <span className="text-white font-semibold">{data.handicapConfig.e_max}</span>
                    </div>
                  )}
                  {data.handicapConfig.league_max && (
                    <div className="bg-white/5 rounded px-3 py-1.5 border border-purple-400/30">
                      {t('admin.createLeague.steps.reviewStep.divisionLabels.leagueWide')} <span className="text-white font-semibold">{data.handicapConfig.league_max}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>)}
          </div>
          
          {/* Example Calculation - individual only */}
          {data.handicapConfig.handicap_type !== 'team_difference' &&
           data.handicapConfig.base_average !== undefined && 
           data.handicapConfig.handicap_percentage !== undefined && (
            <div className="mt-4 bg-purple-600/10 rounded-lg p-3 border border-purple-400/30">
              <p className="text-xs text-purple-200">
                <span className="font-medium">{t('admin.createLeague.steps.reviewStep.exampleHandicap', { handicap: Math.round((data.handicapConfig.base_average - 150) * (data.handicapConfig.handicap_percentage / 100)) })}</span>
              </p>
            </div>
          )}
        </div>
      )}

      {/* Blind Configuration */}
      {data.handicapConfig && (data.handicapConfig.bot_blind_average || data.handicapConfig.bowler_blind_percentage || data.handicapConfig.bowler_blind_include_handicap) && (
        <div className="bg-white/5 rounded-lg p-6 border border-white/10">
          <h3 className="text-lg font-semibold text-white mb-4">👻 {t('admin.createLeague.steps.reviewStep.blindConfiguration')}</h3>
          <div className="grid md:grid-cols-2 gap-4 text-purple-200">
            {data.handicapConfig.bot_blind_average && (
              <div>
                <span className="font-medium">{t('admin.createLeague.steps.reviewStep.botBlindAverage')}</span> <span className="text-white">{data.handicapConfig.bot_blind_average}</span>
              </div>
            )}
            {data.handicapConfig.bowler_blind_percentage && (
              <div>
                <span className="font-medium">{t('admin.createLeague.steps.reviewStep.bowlerBlindPercentage')}</span> <span className="text-white">{data.handicapConfig.bowler_blind_percentage}%</span>
              </div>
            )}
            <div className="md:col-span-2">
              <span className="font-medium">{t('admin.createLeague.steps.reviewStep.includeHandicapInBlind')}</span> <span className="text-white">{data.handicapConfig.bowler_blind_include_handicap ? t('admin.createLeague.steps.reviewStep.yes') : t('admin.createLeague.steps.reviewStep.no')}</span>
            </div>
          </div>
        </div>
      )}

      {/* Packages */}
      {data.packages && data.packages.length > 0 && (
        <div className="bg-white/5 rounded-lg p-6 border border-white/10">
          <h3 className="text-lg font-semibold text-white mb-4">{t('admin.createLeague.steps.reviewStep.packages')}</h3>
          <div className="space-y-3">
            {data.packages.map((pkg, index) => (
              <div key={index} className="bg-white/5 rounded-lg p-4">
                <h4 className="font-semibold text-white">{pkg.name}</h4>
                <p className="text-purple-200 text-sm">{pkg.description}</p>
                <div className="flex gap-4 mt-2 text-sm text-purple-300">
                  <span>{t('admin.createLeague.steps.reviewStep.price')} ${pkg.price}</span>
                </div>
                <div className="mt-2 text-sm text-purple-200">
                  <span className="font-medium">{t('admin.createLeague.steps.reviewStep.includes')}</span> {
                    Array.isArray(pkg.includes) && pkg.includes.length > 0
                      ? pkg.includes.map(squadId => getSquadName(squadId)).join(', ')
                      : t('admin.createLeague.steps.reviewStep.noSquadsSelected')
                  }
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Point System */}
      {data.pointSystem && (
        <div className="bg-white/5 rounded-lg p-6 border border-white/10">
          <h3 className="text-lg font-semibold text-white mb-4">{t('admin.createLeague.steps.reviewStep.pointSystem')}</h3>
          <div className="grid md:grid-cols-2 gap-4 text-purple-200">
            <div>
              <span className="font-medium">{t('admin.createLeague.steps.reviewStep.pointsPerGameWin')}</span> {data.pointSystem.points_per_game_win}
            </div>
            <div>
              <span className="font-medium">{t('admin.createLeague.steps.reviewStep.pointsPerSeriesWin')}</span> {data.pointSystem.points_per_series_win}
            </div>
            <div>
              <span className="font-medium">{t('admin.createLeague.steps.reviewStep.bothGameAndSeries')}</span> {data.pointSystem.points_per_both ? t('admin.createLeague.steps.reviewStep.yes') : t('admin.createLeague.steps.reviewStep.no')}
            </div>
            {data.pointSystem.custom_formula && (
              <div className="md:col-span-2">
                <span className="font-medium">{t('admin.createLeague.steps.reviewStep.customFormula')}</span> {data.pointSystem.custom_formula}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Squads */}
      {data.squads && data.squads.length > 0 && (
        <div className="bg-white/5 rounded-lg p-6 border border-white/10">
          <h3 className="text-lg font-semibold text-white mb-4">{t('admin.createLeague.steps.reviewStep.squads', { count: data.squads.length })}</h3>
          <div className="grid md:grid-cols-2 gap-3">
            {data.squads.map((squad, index) => (
              <div key={index} className="bg-white/5 rounded-lg p-3">
                <h4 className="font-semibold text-white text-sm">{squad.name}</h4>
                <div className="text-purple-200 text-xs">
                  <div>{squad.date}</div>
                  <div>{squad.start_time} - {squad.end_time}</div>
                  <div className="text-purple-300 font-medium">
                    {squad.games_per_squad} {squad.games_per_squad === 1 ? t('admin.createLeague.steps.reviewStep.game') : t('admin.createLeague.steps.reviewStep.games')}
                  </div>
                  {squad.oil_pattern && (
                    <div className="text-blue-300 font-medium mt-1">
                      🎳 {squad.oil_pattern}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Leaderboards */}
      {data.leaderboards && data.leaderboards.length > 0 && (
        <div className="bg-white/5 rounded-lg p-6 border border-white/10">
          <h3 className="text-lg font-semibold text-white mb-4">{t('admin.createLeague.steps.reviewStep.leaderboards', { count: data.leaderboards.length })}</h3>
          <div className="space-y-3">
            {data.leaderboards.map((leaderboard, index) => (
              <div key={index} className="bg-white/5 rounded-lg p-3">
                <h4 className="font-semibold text-white text-sm">{getTranslatedLeaderboardName(leaderboard.name)}</h4>
                <p className="text-purple-200 text-xs mb-2">{leaderboard.description}</p>
                {leaderboard.payoutSettings && leaderboard.payoutSettings.length > 0 && (
                  <div className="text-xs text-purple-300">
                    {t('admin.createLeague.steps.reviewStep.payouts')} {leaderboard.payoutSettings.map(p => 
                      `${p.placement === 1 ? '1st' : p.placement === 2 ? '2nd' : p.placement === 3 ? '3rd' : `${p.placement}th`}: ${
                        p.type === 'money' ? `$${p.amount}` : p.item_desc
                      }`
                    ).join(', ')}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Awards */}
      {data.awards && data.awards.length > 0 && (
        <div className="bg-white/5 rounded-lg p-6 border border-white/10">
          <h3 className="text-lg font-semibold text-white mb-4">{t('admin.createLeague.steps.reviewStep.awards', { count: data.awards.length })}</h3>
          <div className="grid md:grid-cols-2 gap-3">
            {data.awards.map((award, index) => (
              <div key={index} className="bg-white/5 rounded-lg p-3">
                <h4 className="font-semibold text-white text-sm">{award.name}</h4>
                <div className="text-purple-200 text-xs">
                  <div>
                    {award.type === 'money' ? `$${award.amount?.toFixed(2)}` : award.item_desc}
                  </div>
                  <div>
                    {award.per_date && award.overall ? t('admin.createLeague.steps.reviewStep.weeklyAndOverall') : 
                     award.per_date ? t('admin.createLeague.steps.reviewStep.weekly') : t('admin.createLeague.steps.reviewStep.overall')}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Side Events */}
      {data.sideEvents && data.sideEvents.length > 0 && (
        <div className="bg-white/5 rounded-lg p-6 border border-white/10">
          <h3 className="text-lg font-semibold text-white mb-4">{t('admin.createLeague.steps.reviewStep.sideEvents', { count: data.sideEvents.length })}</h3>
          <div className="space-y-3">
            {data.sideEvents.map((event, index) => (
              <div key={index} className="bg-white/5 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-semibold text-white text-sm">{event.name}</h4>
                  <span className={`px-2 py-1 text-xs rounded ${
                    event.type === 'bracket' ? 'bg-red-500/20 text-red-200' :
                    event.type === 'high_game' ? 'bg-blue-500/20 text-blue-200' :
                    event.type === 'high_series' ? 'bg-green-500/20 text-green-200' :
                    event.type === 'mystery_score' ? 'bg-yellow-500/20 text-yellow-200' :
                    'bg-purple-500/20 text-purple-200'
                  }`}>
                    {event.type.replace('_', ' ')}
                  </span>
                </div>
                <div className="text-purple-200 text-xs mb-2">{event.description}</div>
                <div className="flex gap-4 text-xs text-purple-300">
                  <span>{t('admin.createLeague.steps.reviewStep.cost')} ${event.cost.toFixed(2)}</span>
                  <span>{t('admin.createLeague.steps.reviewStep.appliesTo')} {event.applies_to}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Summary */}
      <div className="bg-green-500/20 border border-green-400 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-white mb-2">{t('admin.createLeague.steps.reviewStep.readyToCreate')}</h3>
        <p className="text-green-200 text-sm">
          {t('admin.createLeague.steps.reviewStep.reviewMessage')}
        </p>
      </div>
    </div>
  );
};

export default ReviewStep;